import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { amiGet, amiPost, Card, Loading, NotYet, errMsg } from "@/lib/ami";

function NewStrategy({ defaults, labels, onDone }) {
  const [type, setType] = useState("trend");
  const [name, setName] = useState("");
  const [params, setParams] = useState(defaults.trend);
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const pick = (t) => { setType(t); setParams(defaults[t]); };
  const m = useMutation({
    mutationFn: () => amiPost("/strategies", { name, type, params, stop_loss_pct: sl ? Number(sl) : null, take_profit_pct: tp ? Number(tp) : null }),
    onSuccess: () => { toast.success("Strategie gespeichert"); setName(""); onDone(); }, onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Card title="Neue Strategie" testid="ami-new-strategy">
      <div className="flex flex-wrap gap-2 mb-4">{Object.keys(defaults).map((t) => <button key={t} onClick={() => pick(t)} data-testid={`ami-strat-type-${t}`} className={`px-3 py-1 rounded-full text-xs border border-border ${type === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{labels[t]}</button>)}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="col-span-2"><span className="text-xs text-muted-foreground">Name</span><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="ami-strat-name" /></label>
        {Object.entries(params).map(([k, v]) => (
          <label key={k}><span className="text-xs text-muted-foreground">{k}</span>
            {typeof v === "boolean"
              ? <select value={String(v)} onChange={(e) => setParams({ ...params, [k]: e.target.value === "true" })} className="mt-1 w-full h-10 rounded-md border border-border bg-background px-2 text-sm" data-testid={`ami-strat-param-${k}`}><option value="true">ja</option><option value="false">nein</option></select>
              : <Input value={v} onChange={(e) => setParams({ ...params, [k]: e.target.value === "" || Number.isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value) })} className="mt-1" data-testid={`ami-strat-param-${k}`} />}
          </label>
        ))}
        <label><span className="text-xs text-muted-foreground">Stop-Loss % (optional)</span><Input value={sl} onChange={(e) => setSl(e.target.value)} className="mt-1" data-testid="ami-strat-sl" /></label>
        <label><span className="text-xs text-muted-foreground">Take-Profit % (optional)</span><Input value={tp} onChange={(e) => setTp(e.target.value)} className="mt-1" data-testid="ami-strat-tp" /></label>
      </div>
      <Button className="mt-4" onClick={() => m.mutate()} disabled={!name || m.isPending} data-testid="ami-strat-save">Speichern</Button>
    </Card>
  );
}

function RunForm({ s }) {
  const nav = useNavigate();
  const [bar, setBar] = useState("4h");
  const [inst, setInst] = useState("BTC-USDT-SWAP");
  const m = useMutation({ mutationFn: () => amiPost("/backtests", { strategy_id: s.id, inst, bar }), onSuccess: (r) => nav(`/app/ami/backtesting?id=${r.id}`), onError: (e) => toast.error(errMsg(e)) });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={inst} onChange={(e) => setInst(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid={`ami-bt-inst-${s.id}`}><option>BTC-USDT-SWAP</option><option>BTC-USDT</option></select>
      <select value={bar} onChange={(e) => setBar(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid={`ami-bt-bar-${s.id}`}><option>1h</option><option>4h</option><option>1d</option></select>
      <Button size="sm" onClick={() => m.mutate()} disabled={m.isPending} data-testid={`ami-bt-run-${s.id}`}>{m.isPending ? "Rechnet…" : "Backtest"}</Button>
    </div>
  );
}

export default function AmiStrategies() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ami-strategies"], queryFn: () => amiGet("/strategies") });
  const refresh = () => qc.invalidateQueries({ queryKey: ["ami-strategies"] });
  if (q.isLoading) return <Loading />;
  const { items, defaults, labels } = q.data;
  return (
    <div className="space-y-6" data-testid="ami-strategies">
      <Card title="Strategien" testid="ami-strategy-list">
        {items.length === 0 ? <NotYet text="Noch keine Strategie gespeichert." /> : items.map((s) => (
          <div key={s.id} className="py-3 border-b border-border/60 last:border-0 flex flex-wrap items-center justify-between gap-3" data-testid={`ami-strategy-${s.id}`}>
            <div><div className="font-medium">{s.name} <span className="text-xs text-muted-foreground ml-1">{labels[s.type]}</span></div>
              <div className="text-xs font-mono text-muted-foreground">{JSON.stringify(s.params)}{s.stop_loss_pct ? ` · SL ${s.stop_loss_pct} %` : ""}{s.take_profit_pct ? ` · TP ${s.take_profit_pct} %` : ""}</div></div>
            <div className="flex items-center gap-2"><RunForm s={s} />
              <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => amiPost(`/strategies/${s.id}/delete`).then(refresh)} data-testid={`ami-strat-delete-${s.id}`}>Löschen</Button></div>
          </div>
        ))}
        <p className="mt-3 text-xs text-muted-foreground">Jeder Backtest wird in Entwicklungszeitraum (70 %) und ungesehenen Testzeitraum (30 %) getrennt. Große Unterschiede deuten auf Überanpassung hin.</p>
      </Card>
      <NewStrategy defaults={defaults} labels={labels} onDone={refresh} />
    </div>
  );
}
